// ABOUTME: The small decisions of the feed: money and hours in the app's locale, the salary qualifier, initials for a
// ABOUTME: company without a logo, how many filters are set, how long a contract runs, and where the talent is standing.
import {
  getAppLocaleValue,
  getCurrentLocale,
  getTrans,
  getTransWithParams,
  TranslationKey
} from 'modules/i18n';
import {
  ApplicationStatusFilter,
  JobListingCard,
  JobListingSearchCriteria,
  JobListingSort,
  Point,
  WeekendWork,
  WorkFrequency,
  WorkingWindow,
  WorkplaceMode
} from './types';

/** The BCP 47 locale the app is in, for Intl. */
export const appLocale = (): string => getAppLocaleValue[getCurrentLocale()];

export const formatMoney = (
  amount: number,
  locale: string = appLocale()
): string =>
  new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(
    amount
  );

export const formatKm = (km: number, locale: string = appLocale()): string =>
  new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(km);

export const formatDay = (
  isoDate: string,
  locale: string = appLocale()
): string =>
  new Date(`${isoDate}T00:00:00`).toLocaleDateString(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });

/** "08:00:00" as the clock shows it. */
export const formatClock = (time: string): string => time.slice(0, 5);

/** "up to" for a range, "fixed" for one figure: the qualifier says which kind of number the monthly figure is. */
export const salaryQualifierKey = (
  card: Partial<Pick<JobListingCard, 'monthlySalaryMin' | 'monthlySalaryMax'>>
): TranslationKey =>
  card.monthlySalaryMin === card.monthlySalaryMax
    ? 'jobs.card.fixed'
    : 'jobs.card.upTo';

/** Minutes per week as hours in the app's locale, half hours kept: a range when they differ, one figure when they agree. */
export const hoursLabel = (
  minMinutes: number,
  maxMinutes: number,
  locale: string = appLocale()
): string => {
  const hours = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });
  const min = hours.format(minMinutes / 60);
  const max = hours.format(maxMinutes / 60);
  return min === max ? min : `${min} - ${max}`;
};

/** The first three letters of the first word, the way the design draws a company without a logo. */
export const initialsOf = (name: string): string =>
  name.trim().split(/\s+/)[0].slice(0, 3).toUpperCase();

/** The default the feed opens with: what is still open to me, newest first. */
export const DEFAULT_CRITERIA: JobListingSearchCriteria = {
  applicationStatus: ApplicationStatusFilter.NONE
};

/**
 * The criteria a fresh visit reads the feed with. Also the key the Jobs page's feed (PJL) seeds the first page under,
 * so the listings section's first page is not asked twice.
 */
export const INITIAL_FEED_CRITERIA: JobListingSearchCriteria = {
  ...DEFAULT_CRITERIA,
  sort: JobListingSort.PUBLISHED_AT
};

/**
 * The criteria without what pro-core accepts but does not apply yet: `fitsMyWeek` narrows nothing, so the sheet hides its
 * switch, and a value restored from the session or replayed from a saved search must not stay active unseen.
 */
export const withoutUnservedCriteria = (
  criteria: JobListingSearchCriteria
): JobListingSearchCriteria => {
  const served = { ...criteria };
  delete served.fitsMyWeek;
  return served;
};

/** How many filters the talent set. The sort is not a filter, and the default application status is not a choice. */
export const filterCount = (criteria: JobListingSearchCriteria): number =>
  (Object.keys(criteria) as (keyof JobListingSearchCriteria)[]).filter(key => {
    const value = criteria[key];
    if (key === 'sort') return false;
    if (key === 'applicationStatus')
      return value !== undefined && value !== ApplicationStatusFilter.NONE;
    if (Array.isArray(value)) return value.length > 0;
    return value !== undefined && value !== null && value !== '';
  }).length;

const DAYS_PER_MONTH = 30.4375;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const DAYS_PER_WEEK = 7;

export interface ContractLength {
  unit: 'months' | 'weeks' | 'days';
  count: number;
}

/** The day one calendar month after an ISO day, clamped to the end of a shorter month (31 Jan → 28/29 Feb). */
const oneMonthAfter = (day: number): number => {
  const date = new Date(day);
  const target = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 1)
  );
  const lastOfTarget = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)
  ).getUTCDate();
  target.setUTCDate(Math.min(date.getUTCDate(), lastOfTarget));
  return target.getTime();
};

/**
 * How long a contract runs, first and last day included: to the nearest month from one calendar month on, to the
 * nearest week below that, in days below a week. Nothing for one without an end, or an end before the start.
 */
export const contractLength = (
  startDate: string,
  endDate: string | undefined
): ContractLength | undefined => {
  if (!endDate) return undefined;
  const start = Date.parse(`${startDate}T00:00:00Z`);
  const dayAfterEnd = Date.parse(`${endDate}T00:00:00Z`) + MS_PER_DAY;
  const days = Math.round((dayAfterEnd - start) / MS_PER_DAY);
  if (!(days > 0)) return undefined;
  if (dayAfterEnd >= oneMonthAfter(start)) {
    return {
      unit: 'months',
      count: Math.max(1, Math.round(days / DAYS_PER_MONTH))
    };
  }
  if (days >= DAYS_PER_WEEK) {
    return { unit: 'weeks', count: Math.round(days / DAYS_PER_WEEK) };
  }
  return { unit: 'days', count: days };
};

/** "6 Monate", "2 Wochen", "5 Tage": a contract's length as the detail page labels it. */
export const contractLengthLabel = ({ unit, count }: ContractLength): string =>
  getTransWithParams(`jobs.detail.${unit}` as TranslationKey, { count });

/** Whether a job asks for this day at all: occasionally or regularly. Unknown and never ask for nothing. */
const asksFor = (frequency: WorkFrequency | undefined): boolean =>
  frequency === 'OCCASIONAL' || frequency === 'REGULAR';

/**
 * The weekend days a job asks for, the way the card's line names them: "Sa und Feiertage", "So", nothing at all when it
 * asks for none or nobody said (absent means unknown, and the card claims nothing).
 */
export const weekendDaysLabel = (
  weekend: WeekendWork | undefined
): string | undefined => {
  if (!weekend) return undefined;
  const days = [
    asksFor(weekend.saturday) ? getTrans('jobs.weekend.saturday') : null,
    asksFor(weekend.sunday) ? getTrans('jobs.weekend.sunday') : null,
    asksFor(weekend.holidays) ? getTrans('jobs.weekend.holidays') : null
  ].filter((day): day is string => !!day);
  if (!days.length) return undefined;
  if (days.length === 1) return days[0];
  return `${days.slice(0, -1).join(', ')} ${getTrans('jobs.weekend.and')} ${
    days[days.length - 1]
  }`;
};

/**
 * The card's contract line: "01. Nov. 2026 – 30. Apr. 2027" for a fixed term, "ab 15. Nov. 2026 · unbefristet, mind. 12
 * Monate" for an open-ended one. A card from before PJL without an end reads as starting on its date and nothing more.
 */
export const contractPeriodLabel = (card: JobListingCard): string => {
  const from = formatDay(card.startDate);
  if (card.endDate && !card.unlimitedContract) {
    return getTransWithParams('jobs.card.period', {
      from,
      to: formatDay(card.endDate)
    });
  }
  if (card.unlimitedContract && card.minContractMonths) {
    return getTransWithParams('jobs.card.openEndedMin', {
      from,
      count: card.minContractMonths
    });
  }
  if (card.unlimitedContract)
    return getTransWithParams('jobs.card.openEnded', { from });
  return from;
};

/** Where the work happens, as the card says it: remote and hybrid by name, a place by its city and distance. */
export const workplaceModeOf = (card: JobListingCard): WorkplaceMode =>
  card.workplaceMode ?? (card.remote ? 'REMOTE' : 'ON_SITE');

/**
 * The card's places line: "3 Einsatzorte in Berlin" with "4,2–11,8 km entfernt" under it, one city with its distance, or
 * nothing to say. Before PJL a listing knew one place, which still reads as its city and distance.
 */
export const placesLabel = (
  card: JobListingCard
): { label: string; hint?: string } | undefined => {
  const { locations } = card;
  if (locations && locations.count > 1 && locations.city) {
    const hasRange =
      locations.nearestKm !== undefined && locations.farthestKm !== undefined;
    return {
      label: getTransWithParams('jobs.card.locations', {
        count: locations.count,
        label: locations.city
      }),
      hint: hasRange
        ? getTransWithParams('jobs.card.distanceRange', {
            from: formatKm(locations.nearestKm!),
            to: formatKm(locations.farthestKm!)
          })
        : undefined
    };
  }
  const city = locations?.city ?? card.locationCity;
  const km = locations?.nearestKm ?? card.distanceKm;
  if (!city && km === undefined) return undefined;
  return {
    label: city ?? '',
    hint:
      km === undefined
        ? undefined
        : getTransWithParams('jobs.card.distance', { km: formatKm(km) })
  };
};

/** Fixed windows with the same times on consecutive days, as one row: "Mo–Di 22:00–06:00" (K6). */
export interface WindowGroup {
  days: number[];
  start: string;
  end: string;
}

export const groupWindows = (windows: WorkingWindow[]): WindowGroup[] =>
  [...windows]
    .sort((a, b) => a.dayOfWeek - b.dayOfWeek)
    .reduce<WindowGroup[]>((groups, window) => {
      const last = groups[groups.length - 1];
      if (
        last &&
        last.start === window.start &&
        last.end === window.end &&
        last.days[last.days.length - 1] === window.dayOfWeek - 1
      ) {
        last.days.push(window.dayOfWeek);
        return groups;
      }
      return [
        ...groups,
        { days: [window.dayOfWeek], start: window.start, end: window.end }
      ];
    }, []);

/** "Mo", or "Mo–Mi" for a run of days. */
export const dayRangeLabel = (days: number[]): string => {
  const name = (day: number) =>
    getTrans(`jobs.filters.day.${day}` as TranslationKey);
  return days.length === 1
    ? name(days[0])
    : `${name(days[0])}–${name(days[days.length - 1])}`;
};

/** Why the device gave no position: the talent refused (the settings guide helps), or it could not tell. */
export type LocateFailure = 'denied' | 'unavailable';

export class LocateError extends Error {
  constructor(readonly reason: LocateFailure) {
    super(`no position: ${reason}`);
  }
}

/**
 * Where the talent is standing, once, from the device: the one source of coordinates for a saved place. The browser
 * asks for permission the first time; a refusal rejects as `denied`, so the sheet can point to the same settings
 * guide the shift check-in uses, and anything else as `unavailable`. The options are the check-in's, without high
 * accuracy, which a place to measure a commute from does not need.
 */
export const locateMe = (): Promise<Point> =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new LocateError('unavailable'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      position =>
        resolve({
          lat: position.coords.latitude,
          lon: position.coords.longitude
        }),
      error =>
        reject(
          new LocateError(
            error.code === error.PERMISSION_DENIED ? 'denied' : 'unavailable'
          )
        ),
      { enableHighAccuracy: false, maximumAge: 60000, timeout: 20000 }
    );
  });
